// Centralized icon system (Lucide). Replaces the emoji used across the UI with a consistent,
// tunable vector set. `TypeIcon` maps an item catalog type → icon; `NodeIcon` maps a folder/device
// node → icon. Generic UI icons are re-exported under `Icons` so call sites import from one place.
/* eslint-disable react-refresh/only-export-components -- this module intentionally co-exports icon
   components and the `Icons`/`TypeIcon`/`NodeIcon` maps so call sites import icons from one place. */
import {
  KeyRound,
  Globe,
  SquareTerminal,
  MonitorSmartphone,
  ShieldCheck,
  Network,
  Database,
  Webhook,
  ScrollText,
  Wifi,
  StickyNote,
  File as FileIcon,
  Folder,
  FolderOpen,
  Server,
  HardDrive,
  Router,
  Flame,
  BatteryCharging,
  Camera,
  Cpu,
  Shield,
  Lock,
  Search,
  Settings,
  Copy,
  Eye,
  EyeOff,
  Plus,
  MoreHorizontal,
  X,
  Play,
  Star,
  Trash2,
  Pencil,
  ChevronRight,
  ChevronDown,
  PanelLeft,
  CircleHelp,
  LogOut,
  Download,
  Share2,
  History,
  WifiOff,
  RefreshCw,
  Check,
  Info,
  TriangleAlert,
  User,
  Building2,
  Inbox,
  type LucideIcon,
} from "lucide-react";

/** Item catalog type → icon. */
export const TYPE_ICON: Record<string, LucideIcon> = {
  login: KeyRound,
  website: Globe,
  ssh: SquareTerminal,
  rdp: MonitorSmartphone,
  vpn: ShieldCheck,
  network_device: Network,
  database: Database,
  api_key: Webhook,
  certificate: ScrollText,
  wifi: Wifi,
  secure_note: StickyNote,
  generic: FileIcon,
};

/** Device-node category → icon. */
export const DEVICE_ICON: Record<string, LucideIcon> = {
  Network: Network,
  Server: Server,
  Storage: HardDrive,
  "BMC/iDRAC": Cpu,
  Firewall: Flame,
  UPS: BatteryCharging,
  Camera: Camera,
  Other: Router,
};

export function TypeIcon({ type, className = "h-4 w-4" }: { type: string; className?: string }) {
  const I = TYPE_ICON[type] ?? FileIcon;
  return <I className={className} strokeWidth={1.75} />;
}

export function NodeIcon({
  node,
  className = "h-4 w-4",
}: {
  node: { kind: "folder" | "device"; meta?: { category?: string } | null };
  className?: string;
}) {
  if (node.kind === "folder") return <Folder className={className} strokeWidth={1.75} />;
  const I = DEVICE_ICON[node.meta?.category ?? "Other"] ?? Router;
  return <I className={className} strokeWidth={1.75} />;
}

/** Generic UI icons used across the chrome. */
export const Icons = {
  shield: Shield,
  key: KeyRound,
  lock: Lock,
  search: Search,
  settings: Settings,
  copy: Copy,
  eye: Eye,
  eyeOff: EyeOff,
  plus: Plus,
  more: MoreHorizontal,
  close: X,
  play: Play,
  star: Star,
  trash: Trash2,
  edit: Pencil,
  chevronRight: ChevronRight,
  chevronDown: ChevronDown,
  panelLeft: PanelLeft,
  help: CircleHelp,
  logout: LogOut,
  download: Download,
  share: Share2,
  history: History,
  ssh: SquareTerminal,
  rdp: MonitorSmartphone,
  folder: Folder,
  folderOpen: FolderOpen,
  vaultPersonal: User,
  vaultOrg: Building2,
  shared: Inbox,
  offline: WifiOff,
  refresh: RefreshCw,
  check: Check,
  info: Info,
  warn: TriangleAlert,
} satisfies Record<string, LucideIcon>;
